import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, ChevronLeft } from "lucide-react";

// Imports des composants existants
import ConferenceTitle from "../componants/conference/ConferenceTitle";
import EventSchedule from "../componants/conference/EventSchedule";
import HeroSection from "../componants/conference/HeroSection";
import Interviews from "../componants/conference/Interviews";
import SpeakersSection from "../componants/conference/SpeakersSection";
import { useDocument } from "../hooks/useDocument";

/**
 * Page de Détail d'une Conférence
 * Design amélioré avec gestion des états de chargement et d'erreur.
 */
const Conference = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Récupération des données via votre hook
  const { docResponse } = useDocument("vonference", id);
  const { document, isPending, error } = docResponse;

  // Scroll vers le haut lors de l'ouverture d'une nouvelle conférence
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // --- ÉCRAN DE CHARGEMENT ---
  if (isPending) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 className="w-12 h-12 text-[#1e40af] animate-spin mb-4" />
        <p className="text-slate-400 font-black uppercase text-[10px] tracking-[0.3em]">
          Chargement de l'événement...
        </p>
      </div>
    );
  }

  // --- ÉCRAN D'ERREUR ---
  if (error || !document) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <div className="bg-white p-10 rounded-[3rem] shadow-xl border border-slate-100 max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tighter mb-4">
            Conférence Introuvable
          </h2>
          <p className="text-slate-500 text-sm mb-8 leading-relaxed">
            Désolé, nous ne parvenons pas à charger les détails de cette
            conférence. Elle a peut-être été déplacée ou supprimée.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center gap-2 w-full py-4 bg-[#1e40af] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-blue-800 transition-all shadow-lg"
          >
            <ChevronLeft size={16} /> Retourner aux conférences
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-700">
      {/* SECTION HERO */}
      <section className="relative">
        <HeroSection data={document.hero} />
      </section>

      {/* SECTION PROGRAMME / SESSIONS */}
      <div className="space-y-0">
        {document.sessions?.map((data, idx) => (
          <EventSchedule
            key={data.id || `session-${idx}`}
            sect={`Session ${idx + 1}`}
            date={data.date}
            events={data.events}
            partImg={data.partImg}
            partners={data.partners}
            desc={data.desc}
          />
        ))}
      </div>

      {/* SECTION SPEAKERS */}
      <section className="py-24 ">
        <ConferenceTitle
          title="Nos Speakers"
          description="Découvrez les experts qui animeront nos conférences et discussions pour cette édition."
        />

        <div className="space-y-20">
          {document.speakers1?.length > 0 && (
            <SpeakersSection
              speakersTitle={document.speakers1Title || "Panel d'Experts"}
              speakers={document.speakers1}
            />
          )}

          {document.speakers2?.length > 0 && (
            <SpeakersSection
              speakersTitle={document.speakers2Title || "Intervenants Spéciaux"}
              speakers={document.speakers2}
            />
          )}

          {document.speakers3?.length > 0 && (
            <SpeakersSection
              speakersTitle={document.speakers3Title || "Invités d'honneur"}
              speakers={document.speakers3}
            />
          )}
        </div>
      </section>

      {/* SECTION INTERVIEWS & PROFILS */}
      <section className="py-24 bg-white">
        <ConferenceTitle
          title="Interviews Exclusives"
          description="Découvrez nos entretiens avec des experts du domaine et les profils des participants attendus."
        />

        <div className="mt-12">
          <Interviews
            profilesImage={document.profilesImage}
            profiles={document.profiles}
            interviews={document.interviews}
          />
        </div>
      </section>
    </div>
  );
};

export default Conference;
